// =============================================================================
// FILE: packages/renderer/src/components/undo/FloatingUndoTile.tsx
// PURPOSE: R2 Floating UX Control Tile — always-available Undo/Redo/History/
//   Checkpoint controls. Draggable, collapsible, non-obtrusive.
//   Default position: bottom-right. User can drag anywhere.
// =============================================================================

import { useState, useRef, useEffect, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { Undo2, Redo2, Clock, Star, Lock, Unlock, ChevronDown, X } from 'lucide-react';
import { useDraggableFloat } from '../../hooks/useDraggableFloat.js';
import { useUndoEngine, CATEGORY_META, type HistoryEntry, type Checkpoint } from '../../stores/undoEngine.store.js';
import { useConfidence } from './ConfidenceModal.js';
import { FloatingWindowFrame } from '../windowing/FloatingWindowFrame.js';
import { useFloatingWindowStore } from '../../stores/floatingWindow.store.js';

// ── History Timeline Panel ────────────────────────────────────────────────────
function HistoryPanel({ onClose }: { onClose: () => void }): JSX.Element {
  const { past, future, checkpoints, restoreToCheckpoint, createCheckpoint } = useUndoEngine();
  const confidence = useConfidence();
  const [filter, setFilter] = useState<string>('all');
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom (most recent) on open
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [past.length]);

  const entries = filter === 'all' ? past : past.filter(e => e.category === filter || (filter === 'checkpoints' && e.isCheckpoint));

  const grouped = entries.reduce<Array<{ date: string; items: HistoryEntry[] }>>((acc, entry) => {
    const d = new Date(entry.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const last = acc[acc.length - 1];
    if (last?.date === d) { last.items.push(entry); }
    else { acc.push({ date: d, items: [entry] }); }
    return acc;
  }, []);

  const categories = ['all', 'checkpoints', 'geometry', 'document', 'ai', 'settings', 'workflow', 'report', 'agent', 'automation'];

  return (
    <div
      data-testid="history-panel"
      style={{
        width: '100%', height: '100%',
        background: 'rgba(5,10,24,0.97)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{
        padding: '14px 16px', borderBottom: '1px solid rgba(37,181,168,0.15)',
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
      }}>
        <Clock size={14} style={{ color: '#25B5A8' }} />
        <span style={{ fontSize: 13, fontWeight: 800, color: '#f0f4ff', flex: 1 }}>R2 History Timeline</span>
        <span style={{ fontSize: 10, color: 'rgba(100,140,180,0.5)', fontFamily: 'monospace' }}>
          {past.length} actions · {future.length} redoable
        </span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(148,172,210,0.5)', cursor: 'pointer', padding: 4 }}>
          <X size={14} />
        </button>
      </div>

      {/* Filter bar */}
      <div style={{
        padding: '8px 12px', borderBottom: '1px solid rgba(30,50,80,0.4)',
        display: 'flex', gap: 4, flexWrap: 'wrap', flexShrink: 0,
      }}>
        {categories.slice(0, 6).map(cat => (
          <button key={cat} onClick={() => setFilter(cat)} style={{
            fontSize: 9, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
            background: filter === cat ? 'rgba(37,181,168,0.2)' : 'transparent',
            border: `1px solid ${filter === cat ? 'rgba(37,181,168,0.5)' : 'rgba(30,50,80,0.4)'}`,
            color: filter === cat ? '#5eead4' : 'rgba(148,172,210,0.5)',
            textTransform: 'capitalize', fontWeight: 600, letterSpacing: '0.06em',
          }}>
            {cat}
          </button>
        ))}
      </div>

      {/* Quick actions */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(30,50,80,0.3)', display: 'flex', gap: 8, flexShrink: 0 }}>
        <button
          data-testid="history-create-checkpoint"
          onClick={() => createCheckpoint()}
          style={{
            flex: 1, padding: '7px', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 700,
            background: 'rgba(252,211,77,0.08)', border: '1px solid rgba(252,211,77,0.3)',
            color: '#fcd34d', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          }}
        >
          <Star size={11} /> Create Checkpoint
        </button>
        <button
          data-testid="history-clear"
          onClick={() => confidence.show({
            title: 'Clear All History',
            description: 'This will permanently erase all recorded actions and checkpoints from this session.',
            changeCount: past.length,
            onConfirm: () => useUndoEngine.getState().clearHistory(),
          })}
          style={{
            padding: '7px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 600,
            background: 'transparent', border: '1px solid rgba(248,113,113,0.2)',
            color: 'rgba(248,113,113,0.5)',
          }}
        >
          Clear All
        </button>
      </div>

      {/* Timeline */}
      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '10px 0' }}>
        {grouped.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'rgba(100,140,180,0.35)', fontSize: 12 }}>
            No actions recorded yet.<br />Start working to build your history.
          </div>
        ) : (
          grouped.map(group => (
            <div key={group.date}>
              <div style={{
                padding: '4px 16px', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
                color: 'rgba(100,140,180,0.4)', textTransform: 'uppercase', borderBottom: '1px solid rgba(30,50,80,0.2)', marginBottom: 4,
              }}>
                {group.date}
              </div>
              {group.items.map((entry, i) => {
                const meta = CATEGORY_META[entry.category];
                const isCP = entry.isCheckpoint;
                const cp = isCP ? checkpoints.find(c => c.timestamp === entry.timestamp) : null;
                return (
                  <div
                    key={entry.id}
                    data-testid={`history-entry-${entry.id}`}
                    style={{
                      padding: '7px 16px 7px 14px',
                      borderLeft: `3px solid ${isCP ? '#fcd34d' : `${meta.color}50`}`,
                      marginLeft: 12,
                      marginBottom: 2,
                      background: isCP ? 'rgba(252,211,77,0.05)' : 'transparent',
                      borderRadius: '0 6px 6px 0',
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = isCP ? 'rgba(252,211,77,0.09)' : 'rgba(37,181,168,0.04)'}
                    onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = isCP ? 'rgba(252,211,77,0.05)' : 'transparent'}
                  >
                    {/* Position dot */}
                    <div style={{
                      width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 4,
                      background: isCP ? '#fcd34d' : meta.color,
                      boxShadow: isCP ? '0 0 6px rgba(252,211,77,0.4)' : 'none',
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: isCP ? '#fcd34d' : '#dce8f8', fontWeight: isCP ? 800 : 400, lineHeight: 1.4 }}>
                        {entry.label}
                      </div>
                      {entry.detail && (
                        <div style={{ fontSize: 10, color: 'rgba(148,172,210,0.45)', marginTop: 2 }}>{entry.detail}</div>
                      )}
                      <div style={{ fontSize: 9, color: 'rgba(100,140,180,0.35)', marginTop: 2, fontFamily: 'monospace' }}>
                        {new Date(entry.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        {' · '}
                        <span style={{ color: meta.color }}>{meta.emoji} {meta.label}</span>
                      </div>
                    </div>
                    {/* Restore checkpoint */}
                    {isCP && cp && (
                      <button
                        data-testid={`restore-checkpoint-${cp.id}`}
                        onClick={() => restoreToCheckpoint(cp.id)}
                        title="Restore to this checkpoint"
                        style={{
                          flexShrink: 0, padding: '3px 8px', borderRadius: 5, cursor: 'pointer', fontSize: 9, fontWeight: 700,
                          background: 'rgba(252,211,77,0.12)', border: '1px solid rgba(252,211,77,0.3)',
                          color: '#fcd34d',
                        }}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Footer stats */}
      <div style={{
        padding: '10px 16px', borderTop: '1px solid rgba(30,50,80,0.4)',
        display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0,
      }}>
        {[
          { label: 'Actions', value: past.length },
          { label: 'Checkpoints', value: checkpoints.length },
          { label: 'Redoable', value: future.length },
        ].map(({ label, value }) => (
          <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontSize: 8, letterSpacing: '0.12em', color: 'rgba(100,140,180,0.4)', textTransform: 'uppercase' }}>{label}</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#5eead4', fontFamily: 'monospace' }}>{value}</span>
          </div>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 9, color: 'rgba(100,140,180,0.3)', fontFamily: 'monospace' }}>
          R2 · UE v1
        </span>
      </div>
    </div>
  );
}

// ── Floating Tile ─────────────────────────────────────────────────────────────
export function FloatingUndoTile(): JSX.Element {
  const {
    past, future, locked, panelOpen,
    undo, redo, createCheckpoint, setLocked, openPanel, closePanel,
  } = useUndoEngine();

  const [collapsed, setCollapsed] = useState(false);
  const openManagedWindow = useFloatingWindowStore((s) => s.openWindow);

  const {
    posStyle, freeDrag, onContextMenu, onDragStart, onMouseEnter, onMouseLeave,
    menuEl, hintEl, dragRingEl, elemRef,
  } = useDraggableFloat('r2-undo-tile', {
    position: 'fixed', right: 20, bottom: 32, zIndex: 9500,
  });

  const undoCount = past.length;
  const redoCount = future.length;
  const ACCENT    = '#25B5A8';

  const tile = (
    <div
      ref={elemRef as unknown as React.RefObject<HTMLDivElement>}
      data-testid="floating-undo-tile"
      data-r2-float-tile="true"
      style={{ ...posStyle, userSelect: 'none' }}
      onContextMenu={onContextMenu}
      onMouseDown={onDragStart}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {hintEl}
      {dragRingEl}
      {/* Collapsed pill */}
      {collapsed ? (
        <button
          data-testid="undo-tile-expand"
          onClick={() => setCollapsed(false)}
          title="R2 Command Centre — right-click to reposition"
          style={{
            width: 44, height: 44, borderRadius: '50%', cursor: 'pointer',
            background: locked ? 'rgba(248,113,113,0.15)' : 'rgba(5,10,24,0.95)',
            border: `1px solid ${locked ? 'rgba(248,113,113,0.5)' : `${ACCENT}50`}`,
            boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 12px ${ACCENT}20`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: locked ? '#f87171' : ACCENT,
            backdropFilter: 'blur(12px)',
          }}
        >
          {locked ? <Lock size={16} /> : <Undo2 size={16} />}
        </button>
      ) : (
        <div style={{
          background: 'rgba(4,8,20,0.97)',
          border: `1px solid ${ACCENT}30`,
          borderRadius: 14,
          boxShadow: `0 16px 48px rgba(0,0,0,0.65), 0 0 24px ${ACCENT}10`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden', minWidth: 200,
        }}>
          {/* Drag handle */}
          <div
            style={{
              padding: '10px 14px 8px',
              display: 'flex', alignItems: 'center', gap: 7,
              cursor: freeDrag ? 'grabbing' : 'grab',
              borderBottom: '1px solid rgba(30,50,80,0.4)',
              background: 'rgba(8,14,32,0.6)',
            }}
          >
            <div style={{ display: 'flex', gap: 3, opacity: 0.35 }}>
              {[0,1,2].map(i => <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{[0,1].map(j => <div key={j} style={{ width: 3, height: 3, borderRadius: '50%', background: '#94a3b8' }} />)}</div>)}
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: ACCENT, flex: 1, textTransform: 'uppercase' }}>
              R2 Command Centre
            </span>
            {locked && (
              <span style={{ fontSize: 8, padding: '2px 6px', borderRadius: 3, background: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)', fontWeight: 800 }}>
                LOCKED
              </span>
            )}
            <button onClick={() => setCollapsed(true)} style={{ background: 'none', border: 'none', color: 'rgba(100,140,180,0.4)', cursor: 'pointer', padding: 0 }}>
              <ChevronDown size={13} />
            </button>
          </div>

          {/* Controls grid */}
          <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* Undo + Redo row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              <button
                data-testid="undo-btn"
                onClick={() => undo()}
                disabled={undoCount === 0 || locked}
                title={undoCount > 0 ? `Undo: ${past[past.length - 1]?.label}` : 'Nothing to undo'}
                style={{
                  padding: '9px 10px', borderRadius: 8, cursor: undoCount > 0 && !locked ? 'pointer' : 'not-allowed',
                  background: undoCount > 0 && !locked ? 'rgba(37,181,168,0.1)' : 'rgba(30,50,80,0.1)',
                  border: `1px solid ${undoCount > 0 && !locked ? `${ACCENT}40` : 'rgba(30,50,80,0.3)'}`,
                  color: undoCount > 0 && !locked ? '#5eead4' : 'rgba(100,140,180,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 11, fontWeight: 700,
                  transition: 'all 0.15s',
                }}
              >
                <Undo2 size={13} />
                <span>Undo</span>
                {undoCount > 0 && <span style={{ fontSize: 9, opacity: 0.6 }}>{undoCount}</span>}
              </button>

              <button
                data-testid="redo-btn"
                onClick={() => redo()}
                disabled={redoCount === 0 || locked}
                title={redoCount > 0 ? `Redo: ${future[0]?.label}` : 'Nothing to redo'}
                style={{
                  padding: '9px 10px', borderRadius: 8, cursor: redoCount > 0 && !locked ? 'pointer' : 'not-allowed',
                  background: redoCount > 0 && !locked ? 'rgba(96,165,250,0.1)' : 'rgba(30,50,80,0.1)',
                  border: `1px solid ${redoCount > 0 && !locked ? 'rgba(96,165,250,0.4)' : 'rgba(30,50,80,0.3)'}`,
                  color: redoCount > 0 && !locked ? '#60a5fa' : 'rgba(100,140,180,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, fontSize: 11, fontWeight: 700,
                  transition: 'all 0.15s',
                }}
              >
                <Redo2 size={13} />
                <span>Redo</span>
                {redoCount > 0 && <span style={{ fontSize: 9, opacity: 0.6 }}>{redoCount}</span>}
              </button>
            </div>

            {/* Secondary controls */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5 }}>
              <button
                data-testid="history-btn"
                onClick={() => panelOpen ? closePanel() : openManagedWindow('system-undo-history')}
                title="History Timeline"
                style={{
                  padding: '7px 5px', borderRadius: 7, cursor: 'pointer', fontSize: 10, fontWeight: 700,
                  background: panelOpen ? 'rgba(37,181,168,0.15)' : 'rgba(30,50,80,0.2)',
                  border: `1px solid ${panelOpen ? `${ACCENT}50` : 'rgba(30,50,80,0.35)'}`,
                  color: panelOpen ? ACCENT : 'rgba(148,172,210,0.55)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                }}
              >
                <Clock size={12} />
                <span style={{ fontSize: 8 }}>History</span>
              </button>

              <button
                data-testid="checkpoint-btn"
                onClick={() => createCheckpoint()}
                title="Create Checkpoint"
                style={{
                  padding: '7px 5px', borderRadius: 7, cursor: 'pointer', fontSize: 10, fontWeight: 700,
                  background: 'rgba(252,211,77,0.08)',
                  border: '1px solid rgba(252,211,77,0.25)',
                  color: '#fcd34d',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                }}
              >
                <Star size={12} />
                <span style={{ fontSize: 8 }}>Checkpoint</span>
              </button>

              <button
                data-testid="lock-btn"
                onClick={() => setLocked(!locked)}
                title={locked ? 'Unlock state' : 'Lock state (prevent accidental undos)'}
                style={{
                  padding: '7px 5px', borderRadius: 7, cursor: 'pointer', fontSize: 10, fontWeight: 700,
                  background: locked ? 'rgba(248,113,113,0.12)' : 'rgba(30,50,80,0.2)',
                  border: `1px solid ${locked ? 'rgba(248,113,113,0.4)' : 'rgba(30,50,80,0.35)'}`,
                  color: locked ? '#f87171' : 'rgba(148,172,210,0.55)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                }}
              >
                {locked ? <Lock size={12} /> : <Unlock size={12} />}
                <span style={{ fontSize: 8 }}>{locked ? 'Locked' : 'Lock'}</span>
              </button>
            </div>

            {/* Last action label */}
            {past.length > 0 && (
              <div style={{
                padding: '6px 10px', borderRadius: 6,
                background: 'rgba(8,14,32,0.5)', border: '1px solid rgba(30,50,80,0.3)',
                fontSize: 10, color: 'rgba(148,172,210,0.55)', lineHeight: 1.4,
              }}>
                <span style={{ color: 'rgba(100,140,180,0.4)' }}>Last: </span>
                {past[past.length - 1]!.label.length > 40
                  ? past[past.length - 1]!.label.slice(0, 38) + '…'
                  : past[past.length - 1]!.label}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {createPortal(tile, document.body)}
      <FloatingWindowFrame
        id="system-undo-history"
        title="System Undo"
        group="System"
        defaultRect={{ x: Math.max(20, window.innerWidth - 390), y: 88, width: 348, height: Math.max(460, window.innerHeight - 180) }}
        canMaximize
        keyboardClosable
        important
        accent="#25B5A8"
        contentStyle={{ overflow: 'hidden' }}
        onOpen={openPanel}
        onRestore={openPanel}
        onClose={closePanel}
        onMinimize={closePanel}
      >
        <HistoryPanel onClose={closePanel} />
      </FloatingWindowFrame>
      {menuEl}
    </>
  );
}
